<template>
	<div class="nav profile" :class="isProfileMenuOpen && 'is-open'">
		<button
			class="profile__btn"
			v-on:click="displayProfileMenu"
			data-testid="profile-menu-name-label"
		>
			<span
				class="profile__name-badge"
				data-testid="profile-menu-initials"
			>
				{{ profileMenuResults.userInitials }}
			</span>
			<a
				v-if="messageCenterLinkType() === 'Url'"
				class="profile__badge-count ignoreBadgeClick"
				v-show="profileMenuResults.unreadCount > 0"
				:href="messageCenterURL"
				data-testid="profile-menu-unread-count"
			>
				{{ profileMenuResults.unreadCount }}
			</a>
			<router-link
				v-if="messageCenterLinkType() === 'route'"
				class="profile__badge-count ignoreBadgeClick"
				v-show="profileMenuResults.unreadCount > 0"
				:to="messageCenterURL"
				data-testid="profile-menu-unread-count"
			>
				{{ profileMenuResults.unreadCount }}
			</router-link>
			<span class="profile__name">
				{{ profileMenuResults.userName }}
			</span>
			<span class="profile__expand-icon">
				<i class="icon icon-arrow_down_filled" />
			</span>
		</button>
		<ul
			class="profile__menu"
			v-show="isProfileMenuOpen"
			data-testid="profile-menu-items-menu"
		>
			<li
				class="profile__menu-item"
				v-for="menuItem in profileItems"
				:key="menuItem.name"
			>
				<a
					class="profile__menu-item-link"
					v-if="isUrlLink(menuItem)"
					:href="menuItem.url"
					:target="menuItem.target"
					@click="onMenuItemClick"
					data-testid="profile-menu-accessweb-link"
					>{{ menuItem.name }}
					<span
						class="profile__badge-count"
						v-show="
							profileMenuResults.unreadCount > 0 &&
								menuItem.name === 'Message Center'
						"
					>
						{{ profileMenuResults.unreadCount }}
					</span>
				</a>
				<!-- note that for a route, we're using the route name, not the actual route -->
				<router-link
					class="profile__menu-item-link"
					v-if="!isUrlLink(menuItem)"
					:to="menuItem.url"
					data-testid="profile-menu-router-link"
					@click="onMenuItemClick"
					>{{ menuItem.name }}
					<span
						class="profile__badge-count"
						v-show="
							profileMenuResults.unreadCount > 0 &&
								menuItem.name === 'Message Center'
						"
					>
						{{ profileMenuResults.unreadCount }}
					</span>
				</router-link>
			</li>

			<li class="profile__menu-item">
				<a
					class="profile__menu-item-link"
					v-on:click="logout"
					data-testid="profile-menu-log-off"
					>Log Off</a
				>
			</li>
		</ul>
	</div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
export default {
	name: 'VueProfileMenu',
	data() {
		return {
			isProfileMenuOpen: false
		};
	},
	async created() {
		await this.getProfileMenuResultsAction();
	},
	mounted() {
		document.addEventListener('click', this.clickHandler);
	},
	beforeDestroy() {
		document.removeEventListener('click', this.clickHandler);
	},
	computed: {
		...mapGetters('user', ['appName', 'profileMenuResults']),
		messageCenterURL() {
			let messageCenterItem = this.getProfileMenuItem('Message Center');
			if (messageCenterItem) {
				return messageCenterItem.url;
			}

			return '';
		},
		profileItems() {
			let items;

			if (this.profileMenuResults !== undefined) {
				items = this.profileMenuResults.profileItems;
			}

			return items;
		}
	},
	methods: {
		...mapActions('user', [
			'logoutUserAction',
			'getProfileMenuResultsAction'
		]),
		isUrlLink(menuItem) {
			let result = true;

			if (
				menuItem.linkType &&
				menuItem.linkType.toLowerCase() === 'route'
			) {
				result = false;
			}

			return result;
		},
		logout() {
			//clears limelight state
			this.logoutUserAction();

			//do AccessWeb logout
			window.dispatchEvent(new Event('vuelogout'));
		},
		clickHandler(event) {
			const { target } = event;
			const { $el } = this;
			if (!$el.contains(target) && this.isProfileMenuOpen) {
				this.isProfileMenuOpen = !this.isProfileMenuOpen;
			}
		},
		goToRouteByName(routeName) {
			this.$router.push({ name: routeName });
		},
		displayProfileMenu(event) {
			const { target } = event;

			if (target.classList.contains('ignoreBadgeClick')) {
				return;
			}
			this.isProfileMenuOpen = !this.isProfileMenuOpen;
		},
		getProfileMenuItem(menuTitle) {
			if (!this.profileMenuResults.profileItems) {
				return null;
			}
			return this.profileMenuResults.profileItems.find(
				p => p.name === menuTitle
			);
		},
		messageCenterLinkType() {
			let messageCenterItem = this.getProfileMenuItem('Message Center');
			if (messageCenterItem) {
				return messageCenterItem.linkType;
			}

			return 'Url';
		},
		onMenuItemClick() {
			this.isProfileMenuOpen = false;
		}
	}
};
</script>

<style lang="scss" scoped>
/**
    * [Description]
    *
    * Profile Menu
    * Can use SCSS variables = TRUE
*/

// UI/UX NOTE: Please revisited when the dark theme design is finished
.profile {
	@include header-menu-item('.profile');

	&__name-badge {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		min-width: 26px;
		min-height: 26px;
		width: 26px;
		height: 26px;
		border-radius: $border-radius;
		background: var(--menu-brand-1-selected-bg);
		color: var(--menu-brand-1-fg-3);

		@include mq($from: md-l) {
			margin-right: $gutter-md;
		}
	}

	&__btn {
		@include header-focus-styles;
		@include mq($from: md-l) {
			&:hover {
				background: var(--menu-list-bg-hover);
				color: var(--menu-list-fg-hover);
			}
		}
	}

	&__btn > &__badge-count {
		display: none;
		position: absolute;
		top: 0px;
		left: 20px;
		cursor: pointer;
		text-decoration: none;

		@include mq($from: md-l) {
			display: inherit;
		}

		@include header-focus-styles;
	}

	&__badge-count {
		@include small-badge;
	}

	&__menu-item-link {
		position: relative;

		.profile__badge-count {
			margin-left: $gutter-sm;
		}

		&:focus-within {
			@include header-focus-styles;
		}
	}
}
</style>
